import React, { useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DirectionsRenderer,
  InfoWindow,
} from "@react-google-maps/api";
import { mapLoaderConfig } from "../Services/mapLoaderConfig";
import { googleMapsApiKey } from "../../config/clientsConfig";
import liveTrackService from "../Services/liveTrackService";
import { getIcons } from "../../components/Constant/Constant";
import GeoImages from "../Constant/GeoImages";
import { GiPathDistance } from "react-icons/gi";

const containerStyle = {
  width: "100%",
  height: "100vh",
};

const SingleUserLiveMap = ({ data, selectedPoint , propertyLocationData }) => {
  const [directions, setDirections] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [progressIndex, setProgressIndex] = useState(0);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [locationNames, setLocationNames] = useState(new Map());
  const [geocodingLoading, setGeocodingLoading] = useState(new Set());
  const [mapCenter, setMapCenter] = useState(null);
  const trackingIntervalRef = useRef(null);
  const mapRef = useRef(null);
    const icons = getIcons();

  const { isLoaded } = useJsApiLoader(mapLoaderConfig);

  const [propertyLocations, setPropertyLocations] = useState( propertyLocationData || []);
  const [selectedPropertyMarker, setSelectedPropertyMarker] = useState( null);
 



  // 👉 Convert tripDetails into Google Maps LatLng objects with enhanced data
  const sampleTrackingPoints =
    data?.tripDetails?.map((trip, index) => {
      const message = trip.message.toLowerCase().trim();
      return {
        lat: parseFloat(trip.geoPoint[0]),
        lng: parseFloat(trip.geoPoint[1]),
        message: trip.message,
        time: trip.time,
        duration: trip.duration,
        index: index,
        isStart: message === 'trip started',
        isEnd: message === 'trip ended',
        isStop: message === 'stop',
        isDrive: message === 'drive',
        id: `point-${index}-${trip.geoPoint[0]}-${trip.geoPoint[1]}`,
      };
    }) || [];

    // Convert fullTripLocations into Google Maps LatLng objects User moving directing point full point 
  const fullTripPoints =
    data?.fullTripLocations?.map((point, index) => ({
      lat: parseFloat(point[0]),
      lng: parseFloat(point[1]),
      id: `full-point-${index}-${point[0]}-${point[1]}`,
      index: index
    })) || [];

  // Handle selectedPoint from timeline
  useEffect(() => {
    if (selectedPoint && sampleTrackingPoints.length > 0) {
      console.log("Processing selected point:", selectedPoint);
      
      // Find the matching point in sampleTrackingPoints
      const matchingPointIndex = sampleTrackingPoints.findIndex(point => 
        point.index === selectedPoint.index ||
        (Math.abs(point.lat - selectedPoint.lat) < 0.000001 && 
         Math.abs(point.lng - selectedPoint.lng) < 0.000001)
      );
      
      console.log("Matching point index:", matchingPointIndex);
      
      if (matchingPointIndex !== -1) {
        // Set the selected marker to show info window
        setSelectedMarker(selectedPoint.index);
        
        // Center map on the selected point
        const pointToCenter = sampleTrackingPoints[matchingPointIndex];
        setMapCenter({ lat: pointToCenter.lat, lng: pointToCenter.lng });
        
        // Pan to the location if map is loaded
        if (mapRef.current) {
          mapRef.current.panTo({ lat: pointToCenter.lat, lng: pointToCenter.lng });
          mapRef.current.setZoom(16); // Zoom in for better visibility
        }
      }
    }
  }, [selectedPoint, sampleTrackingPoints.length]);

  // Reverse geocoding function
  const getLocationName = async (lat, lng, pointId) => {
    if (geocodingLoading.has(pointId)) return;
    
    setGeocodingLoading(prev => new Set([...prev, pointId]));
    
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapsApiKey}`
      );
      const data = await response.json();

      if (data.status === "OK" && data.results.length > 0) {
        const result = data.results[0];
        const address = result.formatted_address;
        
        setLocationNames(prev => new Map(prev.set(pointId, {
          fullAddress: address,
          shortAddress: address.split(',').slice(0, 2).join(',').trim(),
          components: result.address_components
        })));
      } else {
        setLocationNames(prev => new Map(prev.set(pointId, {
          fullAddress: "Unknown Location",
          shortAddress: "Unknown Location",
          components: []
        })));
      }
    } catch (error) {
      console.error("Error fetching location name:", error);
      setLocationNames(prev => new Map(prev.set(pointId, {
        fullAddress: "Location not available",
        shortAddress: "Location not available", 
        components: []
      })));
    } finally {
      setGeocodingLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(pointId);
        return newSet;
      });
    }
  };

  // Fetch location names for all points
  useEffect(() => {
    if (sampleTrackingPoints.length > 0) {
      sampleTrackingPoints.forEach(point => {
        if (!locationNames.has(point.id)) {
          getLocationName(point.lat, point.lng, point.id);
        }
      });
    }
  }, [data]);

  // Get marker icon based on trip type with improved logic
  const getMarkerIcon = (point, index) => {
    // First point is always start (regardless of message)
    if (index === 0) {
      return {
        url: GeoImages.startPin,
        scaledSize: new window.google.maps.Size(36, 36),
        anchor: new window.google.maps.Point(18, 18),
      };
    }
    
    // Last point is always end (regardless of message)
    if (index === sampleTrackingPoints.length - 1) {
      return {
        url: GeoImages.endPin,
        scaledSize: new window.google.maps.Size(36, 36),
        anchor: new window.google.maps.Point(18, 18),
      };
    }
    
    // Stop points (middle points with 'stop' message)
    if (point.isStop) {
      return {
       url: GeoImages.parkingPin,
        scaledSize: new window.google.maps.Size(28, 28),
        anchor: new window.google.maps.Point(14, 14),
      };
    }
    
    // Drive points (movement points)
    return {
      url: GeoImages.userPin,
      scaledSize: new window.google.maps.Size(22, 22),
      anchor: new window.google.maps.Point(11, 11),
    };
  };

  // Get property marker icon
  const getPropertyMarkerIcon = () => {
    return {
      url: GeoImages.propertyPin,
      scaledSize: new window.google.maps.Size(32, 40),
      anchor: new window.google.maps.Point(16, 40),
      
    };
  };

  function splitPoints(points, maxWaypoints = 25) {
    const segments = [];
    for (let i = 0; i < points.length - 1; i += maxWaypoints) {
      const segment = points.slice(i, i + maxWaypoints + 1); // include start & end
      segments.push(segment);
    }
    return segments;
  }
  
  // Split points into segments to handle Google Maps waypoint limitations (max 25 waypoints per request)
  function splitPointsForExactPath(points, maxWaypoints = 23) { // Leave room for origin and destination
    const segments = [];
    for (let i = 0; i < points.length - 1; i += maxWaypoints) {
      const segment = points.slice(i, Math.min(i + maxWaypoints + 1, points.length));
      segments.push(segment);
    }
    return segments;
  }
  
  useEffect(() => {
    if (!isLoaded || fullTripPoints.length < 2) return;
  
    // Clear any existing directions
    setDirections([]);
    
    const segments = splitPointsForExactPath(fullTripPoints);
    const allDirections = [];
    let completedSegments = 0;
  
    segments.forEach((segment, segmentIndex) => {
      const directionsService = new window.google.maps.DirectionsService();
      
      // Use all points in segment as waypoints to force exact path
      const waypoints = segment.slice(1, -1).map((point) => ({
        location: { lat: point.lat, lng: point.lng },
        stopover: false, // Don't stop at waypoints, just pass through
      }));
  
      const request = {
        origin: { lat: segment[0].lat, lng: segment[0].lng },
        destination: { lat: segment[segment.length - 1].lat, lng: segment[segment.length - 1].lng },
        waypoints: waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false, // Don't optimize, use exact order
        avoidHighways: false,
        avoidTolls: false,
        region: 'IN', // Set region to India for better local routing
      };
  
      directionsService.route(request, (result, status) => {
        if (status === "OK") {
          allDirections[segmentIndex] = result; // Maintain order
          completedSegments++;
          
          console.log(`✅ Segment ${segmentIndex + 1}/${segments.length} completed`);
  
          // When all segments are completed
          if (completedSegments === segments.length) {
            console.log("🗺️ All direction segments loaded, showing exact path");
            setDirections(allDirections.filter(Boolean)); // Remove any undefined segments
            startTracking(fullTripPoints);
          }
        } else {
          console.error(`❌ Directions failed for segment ${segmentIndex}:`, status);
          // Try with fewer waypoints if too many waypoints error
          if (status === "MAX_WAYPOINTS_EXCEEDED" && waypoints.length > 10) {
            console.log(`🔄 Retrying segment ${segmentIndex} with fewer waypoints...`);
            const reducedWaypoints = waypoints.filter((_, idx) => idx % 2 === 0); // Take every 2nd waypoint
            
            directionsService.route({
              ...request,
              waypoints: reducedWaypoints
            }, (retryResult, retryStatus) => {
              if (retryStatus === "OK") {
                allDirections[segmentIndex] = retryResult;
                completedSegments++;
                
                if (completedSegments === segments.length) {
                  setDirections(allDirections.filter(Boolean));
                  startTracking(fullTripPoints);
                }
              } else {
                console.error(`❌ Retry also failed for segment ${segmentIndex}:`, retryStatus);
                completedSegments++; // Count as completed to avoid hanging
                if (completedSegments === segments.length) {
                  setDirections(allDirections.filter(Boolean));
                  startTracking(fullTripPoints);
                }
              }
            });
          } else {
            completedSegments++; // Count as completed to avoid hanging
            if (completedSegments === segments.length) {
              setDirections(allDirections.filter(Boolean));
              startTracking(fullTripPoints);
            }
          }
        }
      });
    });
  }, [isLoaded, data]);
  

  const startTracking = (path) => {
    let idx = 0;
    setCurrentPosition(path[0]);
    setIsAnimating(true);

    trackingIntervalRef.current = setInterval(() => {
      idx++;
      if (idx < path.length) {
        setCurrentPosition(path[idx]);
        setProgressIndex(idx);
      } else {
        clearInterval(trackingIntervalRef.current);
        setIsAnimating(false);
      }
    }, 500); // Faster animation since we have more points
  };

  const handleMarkerClick = (point, index) => {
    setSelectedMarker(selectedMarker === index ? null : index);
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
  };

  if (!isLoaded) return (
    <div className="flex items-center justify-center h-screen bg-gray-100">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-lg text-gray-600">🗺️ Loading Map...</p>
      </div>
    </div>
  );




  return (
    <div className="relative w-full h-screen">
      {/* Trip Summary Panel */}
      <div className="absolute bottom-8 right-12 z-10 bg-white rounded-full px-3 py-1 shadow-md">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-gray-700">Live</span>
        </div>
      </div>

      
   
            <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter || currentPosition || { lat: 12.9249, lng: 80.1275 }} 
            zoom={15}
            onLoad={onMapLoad}
            options={{
              styles: [
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }]
                }
              ]
            }}
          >
            {/* Road-following line */}
            {directions?.map((dir, index) => (
              <DirectionsRenderer key={index} directions={dir} />
            ))}
    
            {/* Moving vehicle marker */}
            {currentPosition && (
              <Marker
                position={currentPosition}
                icon={{
                  url: GeoImages.userPin,
                  scaledSize: new window.google.maps.Size(48, 48),
                  anchor: new window.google.maps.Point(24, 24),
                }}
                zIndex={1000}
              />
            )}
    
            {/* Trip detail markers */}
            {sampleTrackingPoints.map((point, index) => {
              const isFirstPoint = index === 0;
              const isLastPoint = index === sampleTrackingPoints.length - 1;
              const locationData = locationNames.get(point.id);
              const isLoadingLocation = geocodingLoading.has(point.id);
              const isSelected = selectedMarker === index;
              
              return (
                <Marker
                  key={index}
                  position={{ lat: point.lat, lng: point.lng }}
                  onClick={() => handleMarkerClick(point, index)}
                  icon={getMarkerIcon(point, index)}
                  zIndex={isFirstPoint || isLastPoint ? 999 : isSelected ? 998 : 500}
                >
                  {isSelected && (
                    <InfoWindow
                      position={{ lat: point.lat, lng: point.lng }}
                      onCloseClick={() => setSelectedMarker(null)}
                    >
                      <div className="p-3 min-w-[280px] max-w-[350px]">
                        <div className="flex items-center gap-2 mb-3">
                         <div>
                          {isFirstPoint && <img src={GeoImages.startPin} alt="Start" className="w-6 h-6"/>}
                          {isLastPoint && <img src={GeoImages.endPin} alt="End" className="w-6 h-6"/>}
                          {!isFirstPoint && !isLastPoint && point.isStop && <img src={GeoImages.parkingPin} alt="Stop" className="w-6 h-6"/>}
                          {!isFirstPoint && !isLastPoint && point.isDrive && <img src={GeoImages.userPin} alt="Driving" className="w-6 h-6"/>}
                         </div>
                          <h4 className="font-bold text-gray-800 capitalize text-lg">
                            {isFirstPoint ? 'Trip Started' :
                             isLastPoint ? 'Trip Ended' : 
                             point.message}
                          </h4>
                          {selectedPoint && selectedPoint.index === point.index && (
                            <div className="ml-auto">
                              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                                From Timeline
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <span>🕒</span>
                            <span className="font-medium">Time:</span>
                            <span className="text-blue-600 font-medium">{point.time}</span>
                          </div>
                          
                          {point.duration && (
                            <div className="flex items-center gap-2">
                              <span>⏱</span>
                              <span className="font-medium">Duration:</span>
                              <span className="text-green-600 font-medium">{point.duration}</span>
                            </div>
                          )}
                          
                          <div className="flex items-start gap-2">
                            <span>📍</span>
                            <div className="flex-1">
                              <span className="font-medium">Location:</span>
                              {isLoadingLocation ? (
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                  <span className="text-gray-500 italic">Loading address...</span>
                                </div>
                              ) : locationData ? (
                                <div className="mt-1">
                                  <p className="text-gray-800 font-medium">{locationData.shortAddress}</p>
                                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{locationData.fullAddress}</p>
                                </div>
                              ) : (
                                <p className="text-gray-500 mt-1">Address not available</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span>🔢</span>
                            <span className="font-medium">Step:</span>
                            <span className="text-purple-600 font-medium">{index + 1} of {sampleTrackingPoints.length}</span>
                          </div>
                          
                          <div className="pt-2 border-t border-gray-200 mt-3">
                            <div className="text-xs text-gray-500">
                              <span className="font-medium">Coordinates:</span> {point.lat.toFixed(6)}, {point.lng.toFixed(6)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              );
            })}
    
            {/* Property markers */}
            {propertyLocations.map((property, index) => (
              <Marker
                key={`property-${index}`}
                position={{ lat: property.geoPoint[0], lng: property.geoPoint[1] }}
                icon={getPropertyMarkerIcon()}
                zIndex={400}
                onClick={() => setSelectedPropertyMarker(index)}
              >
                {selectedPropertyMarker === index && (
                  <InfoWindow
                    position={{ lat: property.geoPoint[0], lng: property.geoPoint[1] }}
                    onCloseClick={() => setSelectedPropertyMarker(null)}
                  >
                    <div className="p-3 min-w-[200px] max-w-[300px]">
                      <h4 className="font-bold text-gray-800 text-lg">{property.propertyName}</h4>
                      <p className="text-sm text-gray-600 mt-1">Coordinates: {property.geoPoint[0].toFixed(6)}, {property.geoPoint[1].toFixed(6)}</p>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            ))}
          </GoogleMap>
   


      
      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-10  bg-white rounded-lg shadow-lg px-6 py-3">
        <h4 className="font-semibold text-gray-800 mb-2 text-sm">Legend</h4>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <img src={GeoImages.startPin} alt="Start" className="w-4 h-4"/>
            <span>Trip Started</span>
          </div>
          <div className="flex items-center gap-2">
           <GiPathDistance className="w-4 h-4 text-blue-500"/>
            <span>Driving</span>
          </div>
          <div className="flex items-center gap-2">
            <img src={GeoImages.parkingPin} alt="Stop" className="w-4 h-4"/>
            <span>Stop</span>
          </div>
          <div className="flex items-center gap-2">
            <img src={GeoImages.endPin} alt="End" className="w-4 h-4"/>
            <span>Trip Ended</span>
          </div>
          <div className="flex items-center gap-2">
            <img src={GeoImages.propertyPin} alt="Property" className="w-4 h-4"/>
            <span>Properties</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleUserLiveMap;